import React, { Component, useEffect, useState } from "react";
import setAuthorizationToken from "./utils/SetAuthorizationToken";
import { hasExpiredToken, SESSION_INVALID_EVENT } from "./utils/sessionStatus";
import LoginPage from "./Pages/LoginPage/Form";
import NoRouteFound from "./Pages/PageNotFound/PageNotFound";
import Header from "./Components/Header/Header";
import Activity from "./Pages/Activity/Activity";
import Vehicles from "./Pages/Vehicles/Vehicles";
import VehicleDetails from "./Pages/Vehicle/Vehicle";
import Contacts from "./Pages/Contact/Contact";
import ContactDetails from "./Pages/Contact/Details";
import ActivityEdit from "./Pages/Activity/Edit/Form";
import { Switch, Route, Redirect } from "react-router-dom";
import { NotificationContainer } from "react-notifications";
import { QueryClient, QueryClientProvider } from "react-query";
import { ToastContainer } from "react-toastify";
// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

class ScrollToTop extends Component {
  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      window.scrollTo(0, 0);
    }
  }

  render() {
    return (
      <div className="app-frame">
        <Header />
        {this.props.children}
      </div>
    );
  }
}

function PrivateRoute({ component: Component, authed, ...rest }) {
  return (
    <Route
      {...rest}
      render={(props) =>
        authed === true ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{ pathname: "/login", state: { from: props.location } }}
          />
        )
      }
    />
  );
}

function PublicOnlyRoute({ component: Component, authed, ...rest }) {
  return (
    <Route
      {...rest}
      render={(props) =>
        (authed === false || new URLSearchParams(window.location.search).get("reauth") === "1") ? (
          <Component {...props} />
        ) : (
          <Redirect to={{ pathname: "/", state: { from: props.location } }} />
        )
      }
    />
  );
}

const App = ({ location, history, ...props }) => {
  const authenticated = !!localStorage.fmsToken;
  const [sessionWarning, setSessionWarning] = useState(false);

  useEffect(() => {
    const checkSession = () => {
      if (hasExpiredToken()) setSessionWarning(true);
    };
    const onStorage = (event) => {
      if (event.key !== null && !["fmsToken", "fmsTokenExpiry"].includes(event.key)) return;

      const token = localStorage.getItem("fmsToken");

      if (!token) {
        setAuthorizationToken(false);
        setSessionWarning(true);
      } else if (!hasExpiredToken()) {
        setAuthorizationToken(token);
        setSessionWarning(false);
      } else {
        setSessionWarning(true);
      }
    };
    const onInvalid = () => setSessionWarning(true);
    const onVisibility = () => {
      if (!document.hidden) checkSession();
    };
    checkSession();
    window.addEventListener("focus", checkSession);
    window.addEventListener("storage", onStorage);
    window.addEventListener(SESSION_INVALID_EVENT, onInvalid);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", checkSession);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SESSION_INVALID_EVENT, onInvalid);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ScrollToTop location={location}>
        <NotificationContainer />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          pauseOnHover={false}
          draggable={false}
          limit={1}
        />
        {sessionWarning && location.pathname !== "/login" && (
          <div role="alert" style={{
            margin: "12px", padding: "14px", borderRadius: "8px",
            border: "1px solid #e8b66d", background: "#fff8e8", color: "#463318"
          }}>
            <strong>Sign-in required.</strong> Your session has expired or is no longer valid.
            Unsaved Work Order changes may be lost if this page reloads or redirects.
            <div style={{ marginTop: "8px" }}>
              If your Work Order is still open, you can
              {" "}<a href="/login?reauth=1" target="_blank" rel="noopener noreferrer">sign in again in a new tab</a>
              {" "}and return here to retry Save. Do not refresh or close this page.
            </div>
          </div>
        )}
        <Switch>
          <PrivateRoute
            history={history}
            authed={authenticated}
            location={location}
            path="/"
            exact
            component={Activity}
          />
          <PublicOnlyRoute
            authed={authenticated}
            location={location}
            path="/login"
            component={LoginPage}
          />
          <PrivateRoute
            history={history}
            authed={authenticated}
            location={location}
            exact
            path="/activity/:id"
            component={ActivityEdit}
          />
          <PrivateRoute
            history={history}
            authed={authenticated}
            location={location}
            exact
            path="/contact/:id"
            component={ContactDetails}
          />
          <PrivateRoute
            history={history}
            authed={authenticated}
            location={location}
            path="/contacts"
            component={Contacts}
          />
          <PrivateRoute
            history={history}
            authed={authenticated}
            location={location}
            path="/vehicles"
            component={Vehicles}
          />
          <PrivateRoute
            history={history}
            authed={authenticated}
            location={location}
            path="/vehicle/:id"
            component={VehicleDetails}
          />
          <Route component={NoRouteFound} />
        </Switch>
      </ScrollToTop>
    </QueryClientProvider>
  );
};

export default App;
